// WYVIO UI 1.0 — Documents V3 (preview)
// Route : /app/documents-v3
// Identité visuelle STRICTEMENT alignée avec Pilotage V4 & Planning V3.
// Statique. Aucune motion. Aucune propagation.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, CalendarClock, MessageCircle,
  FileText, Settings, HelpCircle, ArrowRight, ChevronDown, Search,
} from "lucide-react";

import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogoGlyph, LogoWordmark } from "@/branding/Logo";
import { SilhouetteVehicle } from "@/components/wyvio-ui/signature/SilhouetteVehicle";
import { AppSidebar } from "@/components/AppSidebar";

/* ═════════════════════════ Utils ═════════════════════════ */
const guessSilhouette = (name = "") => {
  const v = name.toLowerCase();
  if (/gt3|coupe|coupé|911|m2|m4|corvette|amg gt|f-type|cayman/.test(v)) return "coupe";
  if (/suv|q3|q5|q7|q8|x1|x3|x5|x7|macan|cayenne|glc|gle|gls|3008|5008|2008|rav|kodiaq/.test(v)) return "suv";
  if (/rs6|break|estate|touring|allroad|shooting|avant/.test(v)) return "break";
  return "berline";
};

// Nombre de documents "attendus" par workflow (mock UI-only). 7 par défaut.
const EXPECTED_DOCS = 7;
// Convertit une progression % en nb docs reçus/N pour l'affichage X/7.
const docsFromProgress = (progress = 0) => {
  const n = Math.max(0, Math.min(EXPECTED_DOCS, Math.round((progress / 100) * EXPECTED_DOCS)));
  return n;
};

// Répartit un dossier dans un seau documentaire (proxy UI depuis progress %).
// À récupérer : 0–24 · Reçus : 25–49 · À vérifier : 50–74 · Validés : 75–100.
const bucketOf = (progress = 0, isFinished = false) => {
  if (isFinished || progress >= 95) return "validated";
  if (progress >= 50) return "toCheck";
  if (progress >= 25) return "received";
  return "toCollect";
};

const BUCKETS = {
  toCollect:  { key: "toCollect",  label: "À récupérer", accent: "#F59E0B", accentSoft: "#FEF1D8", txt: "#8A4A00" },
  received:   { key: "received",   label: "Reçus",       accent: "#3B82F6", accentSoft: "#E1ECFF", txt: "#1E40AF" },
  toCheck:    { key: "toCheck",    label: "À vérifier",  accent: "#8B5CF6", accentSoft: "#ECE4FE", txt: "#5B21B6" },
  validated:  { key: "validated",  label: "Validés",     accent: "#B8E600", accentSoft: "#F1FBCB", txt: "#4A6300" },
};

const FR_WEEKDAY = () => new Date().toLocaleDateString("fr-FR", { weekday: "long" }).toUpperCase();
const FR_DAY_MONTH = () => new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long" }).toUpperCase();

/* ═════════════════════════ KPI géant (V4) ═════════════════════════ */
const KpiGiant = ({ value, label, sub, accent, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      "flex flex-col items-start text-left transition-opacity",
      active ? "opacity-100" : "opacity-60 hover:opacity-95",
    ].join(" ")}
    data-testid={testId}
  >
    <div className="text-[56px] font-medium leading-none tracking-[-0.03em] text-black">
      {String(value).padStart(2, "0")}
    </div>
    <div className="mt-3 flex items-center gap-1.5">
      <span className="h-[6px] w-[6px] rounded-full" style={{ background: accent }} />
      <span className="text-[10.5px] uppercase tracking-[0.16em] text-black/60">{label}</span>
    </div>
    <div className="mt-1 text-[11.5px] text-black/40">{sub}</div>
    <div className="mt-4 h-[2px] w-full rounded-full"
         style={{ background: active
            ? `linear-gradient(90deg, ${accent} 0%, ${accent}55 60%, transparent 100%)`
            : `linear-gradient(90deg, ${accent}55 0%, ${accent}22 60%, transparent 100%)` }} />
  </button>
);

/* ═════════════════════════ Ligne dossier ═════════════════════════ */
const DossierRow = ({ dossier, onOpen }) => {
  const silhouette = guessSilhouette(dossier.vehicle_name);
  const bucket = BUCKETS[bucketOf(dossier.progress, dossier.is_finished)];
  const nDocs = docsFromProgress(dossier.progress);
  const pct = Math.max(0, Math.min(100, dossier.progress || 0));

  return (
    <article
      className="relative flex items-center gap-6 pl-4"
      data-testid={`documents-v3-row-${dossier.id}`}
    >
      {/* Ribbon supprimé — le badge bucket porte déjà le statut. */}

      {/* Photo véhicule */}
      <button
        onClick={() => onOpen(dossier.id)}
        className="relative h-[84px] w-[132px] flex-shrink-0 overflow-hidden rounded-2xl bg-[#0C0C0C]"
        data-testid={`documents-v3-photo-${dossier.id}`}
        aria-label={`Ouvrir dossier ${dossier.vehicle_name}`}
      >
        {dossier.vehicle_image ? (
          <img src={dossier.vehicle_image} alt={dossier.vehicle_name}
               className="h-full w-full object-cover" loading="lazy"
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <div className="flex h-full w-full items-center justify-center"
               style={{ background: "radial-gradient(circle at 50% 60%, #1c1c1c 0%, #0a0a0a 75%)" }}>
            <SilhouetteVehicle type={silhouette} variant="dark" className="w-[76%]" />
          </div>
        )}
      </button>

      {/* Infos */}
      <button onClick={() => onOpen(dossier.id)} className="min-w-0 flex-1 text-left">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
          <h3 className="text-[16.5px] font-medium tracking-[-0.005em] text-black">{dossier.vehicle_name}</h3>
          <span className="truncate text-[12.5px] text-black/35">{dossier.client_name}</span>
        </div>
        <div className="mt-2 flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.08em]"
                style={{ background: bucket.accentSoft, color: bucket.txt }}>
            {bucket.label}
          </span>
          <span className="text-[12px] font-mono text-black/45">
            {nDocs}/{EXPECTED_DOCS}
          </span>
        </div>
      </button>

      {/* Barre de progression — anthracite premium + tip lime WYVIO
          (fill lime pur uniquement pour les dossiers "validés"). */}
      <div className="hidden w-[220px] flex-shrink-0 lg:block">
        <div className="mb-1.5 flex items-baseline justify-between">
          <span className="text-[10.5px] uppercase tracking-[0.14em] text-black/40">Progression</span>
          <span className="text-[11.5px] font-medium text-black/70">{pct}%</span>
        </div>
        <div className="relative h-[6px] w-full overflow-hidden rounded-full bg-black/[0.06]">
          {/* Fill principal */}
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-500"
            style={{
              width: `${pct}%`,
              background: bucket.key === "validated"
                ? "linear-gradient(90deg, #C8FF2F 0%, #C8FF2F 100%)"
                : "linear-gradient(90deg, #1F1F1F 0%, #2A2A2A 100%)",
            }}
          />
          {/* Terminaison lime — accent WYVIO discret (hors validés) */}
          {bucket.key !== "validated" && pct > 4 && (
            <div
              className="absolute top-0 h-full w-[6px] rounded-full transition-[left] duration-500"
              style={{
                left: `calc(${pct}% - 6px)`,
                background: "#C8FF2F",
                boxShadow: "0 0 4px rgba(200,255,47,0.55)",
              }}
              aria-hidden
            />
          )}
        </div>
      </div>

      {/* CTA */}
      <button
        onClick={() => onOpen(dossier.id)}
        className="flex-shrink-0 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[12.5px] font-medium text-black/75 transition-colors hover:text-black"
        style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.03), inset 0 0 0 1px rgba(0,0,0,0.05)" }}
        data-testid={`documents-v3-cta-${dossier.id}`}
      >
        Ouvrir le dossier
        <ArrowRight size={13} />
      </button>
    </article>
  );
};

const Skel = () => (
  <div className="flex items-center gap-6 pl-4">
    <div className="h-[84px] w-[132px] animate-pulse rounded-2xl bg-black/[0.04]" />
    <div className="min-w-0 flex-1 space-y-2.5">
      <div className="h-4 w-2/5 animate-pulse rounded-full bg-black/[0.05]" />
      <div className="h-3 w-1/3 animate-pulse rounded-full bg-black/[0.04]" />
    </div>
    <div className="h-2 w-[220px] animate-pulse rounded-full bg-black/[0.04]" />
    <div className="h-9 w-32 animate-pulse rounded-full bg-black/[0.04]" />
  </div>
);

/* ═════════════════════════ MAIN ═════════════════════════ */
export default function DocumentsV3() {
  const navigate = useNavigate();
  const { user, agency } = useAuth();
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("toCheck"); // seau actif par défaut
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api.listDossiers()
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.items || data?.dossiers || []);
        if (!cancel) setDossiers(list.filter((d) => !d.is_finished));
      })
      .catch(() => { if (!cancel) setDossiers([]); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);

  const buckets = useMemo(() => {
    const b = { toCollect: 0, received: 0, toCheck: 0, validated: 0 };
    dossiers.forEach((d) => { b[bucketOf(d.progress, d.is_finished)] += 1; });
    return b;
  }, [dossiers]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return dossiers
      .filter((d) => bucketOf(d.progress, d.is_finished) === filter)
      .filter((d) => !q || (d.vehicle_name || "").toLowerCase().includes(q) || (d.client_name || "").toLowerCase().includes(q))
      .sort((a, b) => (b.progress || 0) - (a.progress || 0))
      .slice(0, 12);
  }, [dossiers, filter, query]);

  return (
    <div className="relative min-h-screen text-black"
         style={{ background: "radial-gradient(1000px 600px at 100% 0%, rgba(200,255,47,0.04) 0%, rgba(200,255,47,0) 45%), linear-gradient(180deg, #FAFAF7 0%, #F5F4EF 100%)" }}
         data-testid="documents-v3-page">
      <AppSidebar agency={agency} user={user} testId="documents-v3-sidebar" />

      <main className="lg:pl-[260px]">
        <div className="mx-auto max-w-[1120px] px-10 py-12">
          {/* Header */}
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.16em] text-black/45">
                {FR_WEEKDAY()} · {FR_DAY_MONTH()}
              </div>
              <h1 className="mt-3 text-[38px] font-semibold leading-[1.02] tracking-[-0.02em] text-black">
                Documents
              </h1>
              <p className="mt-1.5 text-[14px] text-black/50">
                {agency?.name || "wyvio"} · {dossiers.length} dossier{dossiers.length > 1 ? "s" : ""} actif{dossiers.length > 1 ? "s" : ""}
              </p>
            </div>

            {/* Search */}
            <div className="hidden items-center gap-2 rounded-full bg-white px-3.5 py-2 sm:flex"
                 style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.04), inset 0 0 0 1px rgba(0,0,0,0.08)" }}>
              <Search size={14} className="text-black/40" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un dossier…"
                className="w-[220px] bg-transparent text-[13px] text-black placeholder:text-black/35 focus:outline-none"
                data-testid="documents-v3-search"
              />
            </div>
          </div>

          {/* 4 KPIs géants — cliquables (filtre courant) */}
          <section
            className="mt-10 grid grid-cols-2 gap-8 rounded-3xl bg-white/60 px-8 py-8 sm:grid-cols-4 sm:gap-10"
            style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.02), inset 0 0 0 1px rgba(0,0,0,0.04)" }}
            data-testid="documents-v3-kpis"
          >
            {["toCollect", "received", "toCheck", "validated"].map((k) => (
              <KpiGiant
                key={k}
                value={buckets[k]}
                accent={BUCKETS[k].accent}
                label={BUCKETS[k].label}
                sub="dossiers"
                active={filter === k}
                onClick={() => setFilter(k)}
                testId={`kpi-${k}`}
              />
            ))}
          </section>

          {/* Tabs — miroir Pilotage V4 */}
          <div className="mt-12 flex items-baseline gap-6 border-b border-black/[0.06] pb-3">
            <span className="text-[11px] font-medium uppercase tracking-[0.2em] text-black">
              {BUCKETS[filter].label}
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-black/35">
              {buckets[filter]} dossier{buckets[filter] > 1 ? "s" : ""}
            </span>
          </div>

          {/* Rows dossiers */}
          <section className="mt-6 space-y-6" data-testid="documents-v3-rows">
            {loading && [0,1,2,3,4].map((i) => <Skel key={i} />)}
            {!loading && filtered.length === 0 && (
              <div className="py-16 text-center text-[14px] text-black/45">
                Aucun dossier dans cette catégorie.
              </div>
            )}
            {!loading && filtered.map((d) => (
              <DossierRow key={d.id} dossier={d} onOpen={(id) => navigate(`/app/dossier/${id}`)} />
            ))}
          </section>

          <div className="h-24" />
        </div>
      </main>
    </div>
  );
}
